import { Component, OnInit, Input } from '@angular/core';
import * as Chartist from 'chartist';
import { ChartEvent } from '../chartist.component';
import { IFunnel, IAccount, IInbox } from '../../../interfaces/graph-types';
import { Subscription } from 'rxjs';
import { FunnelService } from '../../../services/funnel.service';
import { AccountService } from '../../../services/account.service';

export interface Chart {
  type: string;
  data: Chartist.IChartistData;
  options?: any;
  responsiveOptions?: any;
  events?: ChartEvent;
}

@Component({
  selector: 'volume-stats',
  template: require('./volume-stats.component.pug'),
  styles: [require('./volume-stats.component.css').toString()],
})
export class VolumeStatsComponent implements OnInit {
  @Input()
  funnelId: string;
  @Input()
  accountId: string;
  @Input()
  download: boolean = true;

  funnel: IFunnel;
  account: IAccount;
  sub: Subscription;
  private inboxes: IInbox[] = null;

  error: boolean = false;
  loading: boolean = true;

  public volumeEvents: ChartEvent;
  public optionsVolume: any;

  chartistVolumeStats: any;

  viewTaskCreated = false;
  viewTaskCompleted = false;
  viewEmailSent = true;
  viewOpenFirst = false;
  viewOpenRepeat = false;
  viewClickFirst = false;
  viewClickRepeat = false;
  viewReplies = false;
  viewUnsub = false;
  viewBounce = false;
  viewApplicationStarted = false;
  viewApplicationEnded = false;
  viewApplicationError = false;
  viewProspectCreated = false;

  hasProspectCreated = false;

  serieTaskCreated: any;
  serieTaskCompleted: any;
  serieEmailSent: any;
  serieOpenFirst: any;
  serieOpenRepeat: any;
  serieClickFirst: any;
  serieClickRepeat: any;
  serieReply: any;
  serieUnsub: any;
  serieBounce: any;
  serieJounreyStarted: any;
  serieApplicationEnded: any;
  serieApplicationError: any;
  serieProspectCreated: any;

  colorTaskCreated = '#61d6bb';
  colorTaskCompleted = '#48a08c';

  colorEmailSent = '#a5a5a5';

  colorOpenFirst = '#1694ff';
  colorOpenRepeat = '#0a4373';

  colorClickFirst = '#d2b229';
  colorClickRepeat = '#7d6a19';

  colorReply = '#13d23c';

  colorUnsub = '#ca5cfe';

  colorBounce = '#a02b23';

  colorApplicationStarted = '#5661a9';
  colorApplicationEnded = '#222746';

  colorApplicationError = '#ff0000';

  colorProspectCreated = '#04e8ff';

  volumeStatsData: any;

  selectedInbox: string = null;

  // -------------------------------------------------------------------
  constructor(
    private funnelService: FunnelService,
    private accountService: AccountService,
  ) {}

  // -------------------------------------------------------------------
  ngOnInit() {
    this.chartistVolumeStats = {};
    this.optionsVolume = {
      low: 0,
      //showArea: true,
      axisY: { onlyInteger: true },
      height: '300px',
      plugins: [Chartist.plugins.tooltip()],
    };
    this.volumeEvents = { draw: this.chartDrawVolumeEvent.bind(this) };

    this.queryStats(res => {});
  }

  // -------------------------------------------------------------------
  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  // -------------------------------------------------------------------
  queryStats(callback) {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.loading = true;

    if (this.funnelId) {
      this.sub = this.funnelService
        .stats(this.funnelId)
        .subscribe(funnel => {
          this.funnel = funnel;
          this.prepareVolumeStats(this.funnel.volumeStats);
          this.inboxes = funnel.allInboxes;
          this.loading = false;
          callback();
        });
    } else {
      this.sub = this.accountService
        .stats(this.accountId)
        .subscribe(account => {
          this.account = account;
          this.prepareVolumeStats(this.account.volumeStats);
          this.inboxes = account.inboxes;
          this.loading = false;
          callback();
        });
    }
  }

  // -------------------------------------------------------------------
  inboxClicked(
    inbox: IInbox,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    if (this.loading || !inbox.canView) {
      onsuccess();
      return;
    }

    if (this.selectedInbox == inbox.id) {
      this.selectedInbox = null;
    } else {
      this.selectedInbox = inbox.id;
    }
    this.viewProspectCreated = false;

    this.queryStats(res => {
      onsuccess();
    });
  }

  // -------------------------------------------------------------------
  setSelection(state) {
    this.viewTaskCreated = state;
    this.viewTaskCompleted = state;
    this.viewEmailSent = state;
    this.viewOpenFirst = state;
    this.viewOpenRepeat = state;
    this.viewClickFirst = state;
    this.viewClickRepeat = state;
    this.viewReplies = state;
    this.viewUnsub = state;
    this.viewBounce = state;
    this.viewApplicationStarted = state;
    this.viewApplicationEnded = state;
    this.viewApplicationError = state;
    this.viewProspectCreated = state;
    this.buildSeries();
  }

  // -------------------------------------------------------------------
  prepareVolumeStats(volumeStats) {
    this.volumeStatsData = {};
    this.volumeStatsData['labels'] = (<any>volumeStats).labels;
    this.volumeStatsData['series'] = [];

    this.serieTaskCreated = JSON.parse(
      JSON.stringify((<any>volumeStats).series[0]),
    );
    this.serieTaskCompleted = JSON.parse(
      JSON.stringify((<any>volumeStats).series[1]),
    );
    this.serieEmailSent = JSON.parse(
      JSON.stringify((<any>volumeStats).series[2]),
    );
    this.serieOpenFirst = JSON.parse(
      JSON.stringify((<any>volumeStats).series[3]),
    );
    this.serieOpenRepeat = JSON.parse(
      JSON.stringify((<any>volumeStats).series[4]),
    );
    this.serieClickFirst = JSON.parse(
      JSON.stringify((<any>volumeStats).series[5]),
    );
    this.serieClickRepeat = JSON.parse(
      JSON.stringify((<any>volumeStats).series[6]),
    );
    this.serieReply = JSON.parse(JSON.stringify((<any>volumeStats).series[7]));
    this.serieUnsub = JSON.parse(JSON.stringify((<any>volumeStats).series[8]));
    this.serieBounce = JSON.parse(JSON.stringify((<any>volumeStats).series[9]));
    this.serieJounreyStarted = JSON.parse(
      JSON.stringify((<any>volumeStats).series[10]),
    );
    this.serieApplicationEnded = JSON.parse(
      JSON.stringify((<any>volumeStats).series[11]),
    );
    this.serieApplicationError = JSON.parse(
      JSON.stringify((<any>volumeStats).series[12]),
    );

    if ((<any>volumeStats).series[13]) {
      this.serieProspectCreated = JSON.parse(
        JSON.stringify((<any>volumeStats).series[13]),
      );
    }

    if (this.serieProspectCreated) {
      this.hasProspectCreated = true;
    }

    this.buildSeries();
  }

  colorSerieIndex = null;

  // -------------------------------------------------------------------
  buildSeries() {
    this.colorSerieIndex = [];

    this.volumeStatsData['series'] = [];
    if (this.viewTaskCreated) {
      this.volumeStatsData['series'].push(this.serieTaskCreated);
      this.colorSerieIndex.push(this.colorTaskCreated);
    }
    if (this.viewTaskCompleted) {
      this.volumeStatsData['series'].push(this.serieTaskCompleted);
      this.colorSerieIndex.push(this.colorTaskCompleted);
    }
    if (this.viewEmailSent) {
      this.volumeStatsData['series'].push(this.serieEmailSent);
      this.colorSerieIndex.push(this.colorEmailSent);
    }
    if (this.viewOpenFirst) {
      this.volumeStatsData['series'].push(this.serieOpenFirst);
      this.colorSerieIndex.push(this.colorOpenFirst);
    }
    if (this.viewOpenRepeat) {
      this.volumeStatsData['series'].push(this.serieOpenRepeat);
      this.colorSerieIndex.push(this.colorOpenRepeat);
    }
    if (this.viewClickFirst) {
      this.volumeStatsData['series'].push(this.serieClickFirst);
      this.colorSerieIndex.push(this.colorClickFirst);
    }
    if (this.viewClickRepeat) {
      this.volumeStatsData['series'].push(this.serieClickRepeat);
      this.colorSerieIndex.push(this.colorClickRepeat);
    }
    if (this.viewReplies) {
      this.volumeStatsData['series'].push(this.serieReply);
      this.colorSerieIndex.push(this.colorReply);
    }
    if (this.viewUnsub) {
      this.volumeStatsData['series'].push(this.serieUnsub);
      this.colorSerieIndex.push(this.colorUnsub);
    }
    if (this.viewBounce) {
      this.volumeStatsData['series'].push(this.serieBounce);
      this.colorSerieIndex.push(this.colorBounce);
    }
    if (this.viewApplicationStarted) {
      this.volumeStatsData['series'].push(this.serieJounreyStarted);
      this.colorSerieIndex.push(this.colorApplicationStarted);
    }
    if (this.viewApplicationEnded) {
      this.volumeStatsData['series'].push(this.serieApplicationEnded);
      this.colorSerieIndex.push(this.colorApplicationEnded);
    }
    if (this.viewApplicationError) {
      this.volumeStatsData['series'].push(this.serieApplicationError);
      this.colorSerieIndex.push(this.colorApplicationError);
    }
    if (this.viewProspectCreated && this.hasProspectCreated) {
      this.volumeStatsData['series'].push(this.serieProspectCreated);
      this.colorSerieIndex.push(this.colorProspectCreated);
    }
    this.chartistVolumeStats = JSON.parse(JSON.stringify(this.volumeStatsData));
  }

  // -------------------------------------------------------------------
  toggleViewTaskCreated() {
    this.viewTaskCreated = !this.viewTaskCreated;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewTaskCompleted() {
    this.viewTaskCompleted = !this.viewTaskCompleted;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewEmailSent() {
    this.viewEmailSent = !this.viewEmailSent;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewOpenFirst() {
    this.viewOpenFirst = !this.viewOpenFirst;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewOpenRepeat() {
    this.viewOpenRepeat = !this.viewOpenRepeat;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewClickFirst() {
    this.viewClickFirst = !this.viewClickFirst;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewClickRepeat() {
    this.viewClickRepeat = !this.viewClickRepeat;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewReplies() {
    this.viewReplies = !this.viewReplies;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewUnsub() {
    this.viewUnsub = !this.viewUnsub;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewBounce() {
    this.viewBounce = !this.viewBounce;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewApplicationStarted() {
    this.viewApplicationStarted = !this.viewApplicationStarted;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewApplicationEnded() {
    this.viewApplicationEnded = !this.viewApplicationEnded;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewApplicationError() {
    this.viewApplicationError = !this.viewApplicationError;
    this.buildSeries();
  }
  // -------------------------------------------------------------------
  toggleViewProspectCreated() {
    this.viewProspectCreated = !this.viewProspectCreated;
    this.buildSeries();
  }

  // -------------------------------------------------------------------
  chartDrawVolumeEvent(context: any): void {
    if (this.colorSerieIndex) {
      context.element._node.style.stroke = this.colorSerieIndex[
        context.seriesIndex
      ];
    }
  }
}
